import React, { CSSProperties, useEffect, useRef, useState } from 'react';

import DatasetDisplayValue from 'components/basic/dataset-display-value/DatasetDisplayValue';
import useCopyDataset from 'hooks/useCopyDataset';
import useScadaTagMove from 'hooks/useScadaTagMove';
import Draggable, { DraggableData } from 'react-draggable';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import useChartDialogStore from 'store/chart-dialog-store';
import useDashboardStore from 'store/dashboard-store';
import useDatasetIdentifiersStore from 'store/dataset-identifiers-store';
import useScadaStore from 'store/scada-store';
import useSelectedScadaTagsStore from 'store/selected-scada-tags-store';
import { durationSeconds } from 'utils/time-utils';
import { formatHexColor } from 'utils/utils';
import DatasetTooltipText from '../../../basic/dataset-tooltip-text/DatasetTooltipText';
import { ScadaTagProps } from '../scadaTypes';

const componentsStartSize: Record<number, number> = {
	1: 90.15,
	2: 224.328,
	3: 358.5,
	4: 492.66,
	5: 626.83,
	6: 761,
	7: 895.16,
	8: 1029.32,
	9: 1163.5,
	10: 1297.66,
	11: 1431.8,
	12: 1566,
};

const TOOLTIP_SHOW_DELAY = 500; // ms
const TOOLTIP_HIDE_DELAY = 50; // ms

const ScadaTag = (props: ScadaTagProps) => {
	const { dataset, identifier, model, refTime, parent, gridSize } = props;

	const [isHovered, setIsHovered] = useState(false);
	const [xCoord, setXCoord] = useState(0);
	const [yCoord, setYCoord] = useState(0);
	const [isDragged, setIsDragged] = useState(false);
	const isEditMode = useDashboardStore(state => state.isEditMode);
	const scadaStore = useScadaStore();
	const selectedIdsStore = useDatasetIdentifiersStore();
	const selectedTagsStore = useSelectedScadaTagsStore();
	const { openDialog } = useChartDialogStore();
	useCopyDataset(isHovered, dataset?.title, dataset?.identifier);
	const selectedTags = selectedTagsStore.tags;
	
	const ref = useRef<HTMLDivElement>(null);
	const divRef = useRef<HTMLDivElement>(null);

	const position = useScadaTagMove(ref, props.x, props.y, gridSize, identifier, props.scadaId);
	
	const { height, width } = divRef.current
		? divRef.current.getBoundingClientRect()
		: { height: 0, width: 0 };

	const identifierParts = identifier.split(':');
	const id = `scada-label-${identifierParts[1]}-${identifierParts[0]}`;
	const loading = !dataset;

	const datasetData = (dataset?.data.length! || 0) > 0 ? dataset?.data[0] : null;
	const time = datasetData?.key ? new Date(datasetData.key as string) : null;

	const delayDuration = durationSeconds(
		model.maxDelayTime,
		model.maxDelayTimeUnit,
	);

	const noData = !datasetData;
	const staleTag = (+(refTime || new Date()) - +time!!) / 1000 > delayDuration;

	const bgColor = getTagColor(props.bgColor, loading, noData, staleTag);

	const tagStyle = {
		left: `${position.x}%`,
		top: `${position.y}%`,
		width,
		height,
		whiteSpace: 'pre-wrap',
	} as CSSProperties;

	useEffect(() => {
		if (dataset && ref.current) {
			ref.current.dataset.identifier = identifier;
			ref.current.dataset.title = dataset.title;
			ref.current.dataset.scadaId = props.scadaId;
		}
	}, [ref, dataset]);

	useEffect(() => {
		props.onCoordChange(position.x, position.y);
	}, [position.x, position.y]);

	const onClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (e.ctrlKey) {

			if (selectedTags.includes(ref.current!)) {
				selectedTagsStore.removeTag(ref.current!);
				selectedIdsStore.removeIdentifier(identifier);
				scadaStore.setLastClick(new Date().getTime());
				return;
			}	

			selectedTagsStore.addTag(ref.current!);
			selectedIdsStore.addIdentifier(identifier);
			scadaStore.setLastClick(new Date().getTime());
		}
	};

	const onDbClick = () => {
		const el = divRef?.current;
		if (selectedTags.includes(ref.current!)) {
			openDialog({ refTime: model.date?.toISOString() });
			return;
		}

		el!.classList.add('selectedLabel');
		selectedTagsStore.addTag(ref.current!);
		selectedIdsStore.addIdentifier(identifier);
		openDialog({ refTime: model.date?.toISOString() });
	};

	const calculateLabelTransform = (
		imageWidth: number,
		componentSize: number,
	) => {
		const labelWidth = imageWidth / componentsStartSize[componentSize];
		return labelWidth;
	};

	const getLabelXY = (dragData: DraggableData) => {
		const { left: labelX, top: labelY } = ref.current!.getBoundingClientRect();

		if (labelX && labelY && parent.width && parent.height) {
			const relX = labelX - parent.left + dragData.deltaX;
			const relY = labelY - parent.top + dragData.deltaY;
			let pX = Math.round((relX / parent.width) * 1000) / 10;
			let pY = Math.round((relY / parent.height) * 1000) / 10;
			pX = pX <= 0 ? 0 : pX;
			pY = pY <= 0 ? 0 : pY;
			return { x: pX, y: pY };
		}

		return { x: 0, y: 0 };
	};	

	const onDragStart = (dragData: DraggableData) => {
		setIsDragged(true);
		onDrag(dragData);
	};

	const onDrag = (dragData: DraggableData) => {
		const { x, y } = getLabelXY(dragData);
		setXCoord(x);
		setYCoord(y);
	};

	const onStop = (dragData: DraggableData) => {
		const { x, y } = getLabelXY(dragData);
		setXCoord(x);
		setYCoord(y);
		setIsDragged(false);
		scadaStore.updateScadaLabelXY(props.scadaId, identifier, x, y);
	}

	return (
		<>
			<Tooltip
				id={`tooltip-${props.scadaId}-${id}`}
				className='z-1'
				place='right'
			/>
			<Draggable
				nodeRef={ref}
				bounds='parent'
				scale={1}
				grid={[
					(parent.width / 100) * gridSize,
					(parent.height / 100) * gridSize,
				]} // grid by ( 1% x gridSize ) image width and height
				disabled={!isEditMode}
				onDrag={(_, dragData) => onDrag(dragData)}
				onStart={(_, dragData) => onDragStart(dragData)}
				onStop={(_, dragData) => onStop(dragData)}>
				<span
					ref={ref}
					style={{ position: 'absolute', ...tagStyle }}
					data-tooltip-id={`tooltip-${props.scadaId}-${id}`}
					data-tooltip-content={`x: ${xCoord}%; y: ${yCoord}%`}
					data-tooltip-hidden={!isDragged}
					data-x={xCoord}
					data-y={yCoord}>
					<div
						id={id}
						ref={divRef}
						onClick={onClick}
						onDoubleClick={onDbClick}
						onMouseEnter={() => setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
						data-tooltip-id={`#${id}`}
						data-tooltip-delay-hide={TOOLTIP_HIDE_DELAY}
						data-tooltip-delay-show={TOOLTIP_SHOW_DELAY}
						className={`scada-label${selectedIdsStore.ids.has(identifier) ? ' selectedLabel' : ''}`}
						style={{
							backgroundColor: bgColor,
							fontSize: `${props.fontSize}px`,
							transform: `scale(${calculateLabelTransform(
								props.parent.width,
								model.size,
							)})`,
							transformOrigin: 'top left',
						}}>
						<DatasetDisplayValue
							loading={loading}
							dataset={dataset}
							isData={!noData}
							data={datasetData}
							truncateSize={props.model.truncateSize}
						/>
					</div>
				</span>
			</Draggable>
			{!isDragged && !loading && (
				<Tooltip id={`#${id}`} className='z-1'>
					<DatasetTooltipText
						data={datasetData}
						time={time!!}
						dataset={dataset!!}
						hide={loading}
					/>
				</Tooltip>
			)}
		</>
	);
}

function getTagColor(
	bgColor: string,
	loading: boolean,
	noData: boolean,
	stale: boolean,
) {
	if (loading) return '#b0fc8b';
	if (noData || stale) return '#fd6565';
	return formatHexColor(bgColor);
}

export default ScadaTag;